// @ts-nocheck
/**
 * Catering Variant Drawer — standalone ES module.
 * Listens for `catering:open-variant-drawer` custom event, opens the global
 * drawer with a variant-selector UI, and dispatches `catering:add-to-cart`
 * when the merchant picks a variant.
 *
 * Loaded via <script type="module">; its only @theme import is the shared
 * availability endpoint paths.
 */

import { AVAILABILITY_API } from '@theme/ea-api-endpoints';

// ── Helpers ──────────────────────────────────────────────────────────────────

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str || '';
  return div.innerHTML;
}

/** Cents in the storefront's money format — see `cateringFormatMoney` in snippets/catering-i18n.liquid. */
function formatPrice(cents) {
  if (typeof window.cateringFormatMoney === 'function') return window.cateringFormatMoney(cents);
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Translated copy from snippets/catering-i18n.liquid, `[name]` placeholders filled from
 * `vars`; the English `fallback` when the key is missing (EA-4364). Mirrors `i18n` in
 * catering-cart.js.
 */
function i18n(key, fallback, vars) {
  const value = window.cateringI18n?.[key];
  let text = typeof value === 'string' && value && !value.startsWith('translation missing') ? value : fallback;
  for (const [name, replacement] of Object.entries(vars || {})) {
    text = text.split(`[${name}]`).join(String(replacement));
  }
  return text;
}

/**
 * The storefront root for this request ('/', '/en-ca/', '/fr-ca/'). Without it the
 * product JSON comes back in the primary market's language (EA-4364). Mirrors
 * `storefrontRoot` in catering-quick-view.js.
 * @returns {string} Always ends in '/'.
 */
function storefrontRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

// ── Variant card HTML ────────────────────────────────────────────────────────

/**
 * The name a variant card shows. Shopify titles the lone variant of an option-less product
 * "Default Title" — a placeholder, never customer copy (EA-3984). Such a product reaches
 * this drawer only when its one variant can't be had, and the card then names the product.
 */
function variantDisplayName(product, variant) {
  const onlyDefault = product.variants.length === 1 && variant.title === 'Default Title';
  return onlyDefault ? product.title : variant.title;
}

function buildVariantCardHtml(product, variant, isSelected, productImages) {
  const name = variantDisplayName(product, variant);
  const selectable = isVariantSelectable(variant);
  const cardClasses = ['catering-variant-card'];
  if (isSelected) cardClasses.push('is-selected');
  if (!selectable) cardClasses.push('is-unavailable');
  const imgSrc = (variant.featured_image && variant.featured_image.src)
    ? variant.featured_image.src
    : (productImages && productImages.length > 0 ? productImages[0] : '');

  const imgHtml = imgSrc
    ? `<img class="catering-variant-card__media" src="${escapeHtml(imgSrc)}" alt="${escapeHtml(name)}" width="64" height="64" loading="lazy">`
    : `<div class="catering-variant-card__media catering-variant-card__media--placeholder"></div>`;

  const note = variantNote(variant);
  const noteHtml = note ? `<span class="catering-variant-card__note">${escapeHtml(note)}</span>` : '';

  return `
    <div
      data-cvd-variant="${variant.id}"
      class="${cardClasses.join(' ')}"
      role="button"
      tabindex="${selectable ? '0' : '-1'}"
      aria-selected="${isSelected}"
      aria-disabled="${!selectable}"
    >
      ${imgHtml}
      <div class="catering-variant-card__body">
        <span class="catering-variant-card__name">${escapeHtml(name)}</span>
        <span class="catering-variant-card__price">${formatPrice(variant.price)}</span>
        ${noteHtml}
      </div>
    </div>`;
}

// ── Full drawer content HTML ─────────────────────────────────────────────────

function buildDrawerContentHtml(product, selectedVariantId) {
  const productImages = product.images || [];
  const variantCardsHtml = product.variants
    .map((v) => buildVariantCardHtml(product, v, v.id === selectedVariantId, productImages))
    .join('');

  // Enabled only when a selectable variant is currently chosen.
  const btnDisabled = !selectedVariantId;

  return `
    <div data-catering-variant-drawer data-cvd-product-id="${product.id}" class="catering-variant-drawer">
      <div class="catering-variant-drawer__body">
        <h3 class="catering-variant-drawer__title">${escapeHtml(product.title)}</h3>
        <div data-cvd-variant-list class="catering-variant-drawer__list">
          ${variantCardsHtml}
        </div>
      </div>
      <div class="catering-variant-drawer__footer">
        <button
          data-cvd-add-to-cart
          type="button"
          class="catering-variant-drawer__add"
          ${btnDisabled ? 'disabled' : ''}
        >${escapeHtml(i18n('variant_drawer.add', 'Add'))}</button>
      </div>
    </div>`;
}

// ── State ────────────────────────────────────────────────────────────────────

let _currentProduct = null;
let _selectedVariantId = null;
let _originProductId = null;
let _pendingQuantity = 1;
let _inventory = null; // { [variantId]: { tracked, policy, qty } } from the card
let _reserved = null;  // { [variantId]: qty already in the in-memory cart }

// Per-handle product cache so a product's variants are fetched only once per
// page session — subsequent clicks open the drawer immediately.
const _productCache = new Map();

/**
 * Monotonic token for openVariantDrawer(). Each open captures it and re-checks after every
 * await; a run whose token no longer matches has been superseded by a later click and
 * bails without touching shared state or opening anything.
 */
let _openSeq = 0;

/** Signal that the variant fetch has settled, so the ATC button loader stops. */
function dispatchFetchEnd() {
  document.dispatchEvent(new CustomEvent('catering:variant-fetch-end'));
}

// ── Inventory / selectability ─────────────────────────────────────────────────

/**
 * The inputs a variant's availability is judged on: the originating card's inventory
 * table, what is already in the cart, the quantity asked for, and the selected store's
 * capabilities. The open drawer's come from module state; the quick view passes its own
 * through `window.EACateringVariantRules` (EA-4222), so both surfaces apply ONE rule.
 *
 * @returns {{inventory: Object|null, reserved: Object|null, quantity: number, capabilities: Map|null}}
 */
function drawerContext() {
  return { inventory: _inventory, reserved: _reserved, quantity: _pendingQuantity, capabilities: _capabilities };
}

/**
 * Total stock a variant can ever fulfil. Untracked inventory or a "continue"
 * (oversell) policy means unlimited; otherwise the tracked stock count.
 */
function getVariantStock(variant, inventory = _inventory) {
  const info = inventory && inventory[variant.id];
  if (!info || !info.tracked || info.policy === 'continue') return Infinity;
  return Math.max(0, parseInt(info.qty, 10) || 0);
}

/**
 * Quantity still available to ADD now = total stock minus what's already in the
 * in-memory cart for this variant (two-way binding with the sticky cart).
 */
function getVariantRemaining(variant, ctx = drawerContext()) {
  const total = getVariantStock(variant, ctx.inventory);
  if (total === Infinity) return Infinity;
  const reserved = (ctx.reserved && ctx.reserved[variant.id]) || 0;
  return Math.max(0, total - reserved);
}

/**
 * Selectable when available, in stock for the requested qty, AND fulfillable by the
 * shopper's chosen method.
 */
function isVariantSelectable(variant, ctx = drawerContext()) {
  return (
    Boolean(variant.available) &&
    getVariantRemaining(variant, ctx) >= ctx.quantity &&
    isVariantFulfillable(variant, ctx)
  );
}

/**
 * Why a variant can't be picked, as shopper-facing copy — '' when it can.
 *
 * "Out of stock" only when there's genuinely no stock (or it's unavailable). When the
 * stock is fine but the chosen method isn't, the method it CAN be had by is named: a
 * dead card becomes an actionable one, since switching method in the store selector is
 * a cheaper fix than picking another size. When stock exists but the requested quantity
 * can't be met, what can still be added is shown — total stock minus what the cart already
 * holds, the same count the selectable check judges on. When the cart already holds all of
 * it, the total cap is shown instead of "Only 0".
 */
function variantNote(variant, ctx = drawerContext()) {
  const stock = getVariantStock(variant, ctx.inventory);
  if (!variant.available || stock <= 0) return i18n('quick_view.out_of_stock', 'Out of stock');
  if (!isVariantFulfillable(variant, ctx)) {
    const instead = fulfillableInsteadBy(variant, ctx);
    if (instead === 'pickup') return i18n('variant_drawer.pickup_only', 'Pickup only');
    if (instead === 'delivery') return i18n('variant_drawer.delivery_only', 'Delivery only');
    return i18n('variant_drawer.not_available', 'Not available');
  }
  if (!isVariantSelectable(variant, ctx)) {
    const remaining = getVariantRemaining(variant, ctx);
    const count = remaining > 0 ? remaining : stock;
    return count === 1
      ? i18n('variant_drawer.only_count_available_one', 'Only [count] available', { count })
      : i18n('variant_drawer.only_count_available_other', 'Only [count] available', { count });
  }
  return '';
}

// ── Fulfillment capability ───────────────────────────────────────────────────
// A catering product can be pickup-only or delivery-only. The availability API knows
// which, so the variants that don't match the shopper's chosen method are ruled out
// BEFORE they can pick one — rather than letting the choice through and failing it at
// "Review & Checkout", by which point they've committed to a variant.

const AVAILABILITY_TIMEOUT_MS = 10000;
const STORE_SELECTION_KEY = 'ea-store-availability';

/** externalVariantId (numeric) -> { delivery, pickup, shipment, flex }, for the open drawer. */
let _capabilities = null;
/** `${ids}:${storeId}:${date}:${orderType}` -> capability map, so reopening doesn't re-ask. */
const _capabilityCache = new Map();

/**
 * The shopper's saved store selection. Read fresh each time — it can change between
 * two adds, and the capability answer depends on the store, the date and the method.
 * Import-free by design, like catering-cart.js, so the shape is re-read rather than
 * imported.
 */
function readStoreSelection() {
  let state = null;
  try {
    const raw = window.localStorage.getItem(STORE_SELECTION_KEY);
    if (raw) state = JSON.parse(raw);
  } catch { state = null; }
  if (!state || typeof state !== 'object') state = {};
  let store = state.store;
  if (typeof store === 'string') {
    try { store = JSON.parse(store); } catch { store = {}; }
  }
  return { ...state, store: store || {} };
}

/**
 * The selected store's id as v4 takes it, or null when no store is selected.
 *
 * Only a positive integer is a store. The header saves a selection with no store behind it
 * as `storeId: ''`, and `Number('')` and `Number(null)` are both 0 — so a finite-number
 * test alone would send "no store" as store 0, which v4 answers with every capability
 * false (measured 2026-09-26): every size struck, Add closed.
 */
function selectedStoreId(selection) {
  const id = Number(selection.storeId);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * The fulfillment method the capability check is judged against — 'delivery' | 'pickup'.
 * Anything else (shipment, or nothing selected) yields '' and the check is skipped, since
 * there is no method to rule variants out for.
 */
function selectedFulfillmentType() {
  const type = String(readStoreSelection().fulfillmentType || '').toLowerCase();
  return type === 'pickup' || type === 'delivery' ? type : '';
}

/**
 * The numeric marketplace id behind a variant, from the card's inventory island.
 * The metafield is country-prefixed ('US-18517') but the endpoint takes plain numbers,
 * so only the trailing digits go out. Mirrors toMarketplaceProductId in catering-cart.js.
 */
function externalIdOf(variant, inventory = _inventory) {
  const raw = inventory && inventory[variant.id] && inventory[variant.id].externalVariantId;
  if (raw == null || raw === '') return null;
  const match = String(raw).match(/(\d+)\s*$/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

/**
 * Whether a variant can be fulfilled by the shopper's chosen method.
 *
 * FAILS OPEN — an unknown answer means selectable. No store selected, a request that
 * failed, a variant with no marketplace id, or a response that didn't mention it all leave
 * the variant available. Blocking on a missing answer would strand shoppers on a product
 * that is probably fine, and the "Review & Checkout" availability check is the real gate;
 * this only spares them a choice it would reject.
 */
function isVariantFulfillable(variant, ctx = drawerContext()) {
  const type = selectedFulfillmentType();
  if (!type || !ctx.capabilities) return true;
  const id = externalIdOf(variant, ctx.inventory);
  if (id == null) return true;
  const caps = ctx.capabilities.get(id);
  if (!caps) return true;
  return caps[type] !== false;
}

/**
 * The method a variant CAN be fulfilled by, when it can't be fulfilled by the chosen one —
 * used for the "Pickup Only" / "Delivery Only" note. Null when it supports neither, which
 * is a plain unavailability rather than a method mismatch.
 */
function fulfillableInsteadBy(variant, ctx = drawerContext()) {
  const id = externalIdOf(variant, ctx.inventory);
  const caps = id == null ? null : ctx.capabilities?.get(id);
  if (!caps) return null;
  if (caps.pickup === true) return 'pickup';
  if (caps.delivery === true) return 'delivery';
  return null;
}

/**
 * Read the per-product capabilities out of an availability response.
 *
 * `products[].fulfillmentCapabilities` is the documented source and is read first. The
 * per-method arrays (`delivery[]` / `pickup[]`, keyed by the same `productId`) are read as
 * a fallback for a response that omits `products[]` — that's the shape every other
 * consumer in this theme parses (see ea-bundle-builder-drawer.js / personalization-drawer.js).
 *
 * The order matters (EA-3904, measured 2026-09-26). Store-scoped, the capabilities are the
 * store's own method support — stable however the ids are batched, and the same answer the
 * cart endpoint enforces at Review & Checkout. The arrays are not a substitute: a
 * delivery-only product's pickup[] entry can carry `arrangementsAvailable: true`, which the
 * fallback reads as pickup-able while checkout refuses it (ORDER_TYPE_NOT_SUPPORTED).
 *
 * @param {any} data
 * @returns {Map<number, {delivery?: boolean, pickup?: boolean}>}
 */
function readCapabilities(data) {
  const caps = new Map();
  if (!data || typeof data !== 'object') return caps;

  if (Array.isArray(data.products)) {
    for (const entry of data.products) {
      const id = Number(entry?.productId ?? entry?.id);
      if (!Number.isFinite(id)) continue;
      const fc = entry.fulfillmentCapabilities;
      if (fc && typeof fc === 'object') caps.set(id, { ...fc });
    }
  }
  if (caps.size > 0) return caps;

  for (const method of ['delivery', 'pickup']) {
    const list = data[method];
    if (!Array.isArray(list)) continue;
    for (const entry of list) {
      const id = Number(entry?.productId);
      if (!Number.isFinite(id)) continue;
      const existing = caps.get(id) || {};
      existing[method] = entry.available === true || entry.arrangementsAvailable === true;
      caps.set(id, existing);
    }
  }
  return caps;
}

/**
 * Load the capability map for a product's variants into `_capabilities`.
 *
 * Cached per product + store + date + method, so reopening the same drawer doesn't re-ask
 * while any of those is unchanged. Asks nothing, and returns null, when no store is
 * selected. Never throws: on any failure `_capabilities` is left null and every variant
 * stays selectable (see isVariantFulfillable).
 *
 * @param {Object} product product JSON from /products/{handle}.js
 * @returns {Promise<void>}
 */
async function loadCapabilities(product, inventory) {
  const type = selectedFulfillmentType();
  if (!type) return null; // nothing to judge against

  const productIds = [];
  for (const variant of product.variants || []) {
    const id = externalIdOf(variant, inventory);
    if (id != null && !productIds.includes(id)) productIds.push(id);
  }
  if (productIds.length === 0) return null;

  const selection = readStoreSelection();
  const store = selection.store || {};

  // No store, no question (EA-3904). v4 answers for ONE store per request, and asked
  // without one it picks that store itself out of however many serve the zip (verified
  // 2026-09-15: `storeId: null` and no `storeId` at all come back identical). Those
  // capabilities would be an arbitrary store's, not this shopper's — so "no store" is
  // "no verdict", which this drawer already treats as fail-open (isVariantFulfillable).
  // Review & Checkout refuses to run without a store (precheckCateringCart in
  // catering-cart.js) and judges the cart against it there.
  const storeId = selectedStoreId(selection);
  if (storeId == null) return null;

  // Built ONCE and used for both the request and its cache key, so the two can't drift.
  // A key listing fewer inputs than the body is a cache that answers questions it was
  // never asked: the same store, date and method can be reached from a different zip
  // code (one store serves many), and the answer for delivery differs between them — so
  // a key without the zip would hand back the previous zip's capabilities and enable a
  // variant this address can't get, or hide one it can.
  const request = {
    productIds,
    date: selection.deliveryDate || '',
    country: selection.country || 'US',
    zipCode: selection.zipCode || '',
    // Street, city and state come off the saved store object, matching the payload
    // #requestCartAvailability sends in assets/ea-cart-drawer.js.
    address1: store.address1 || '',
    address2: '',
    city: store.cityName || '',
    state: store.stateCode || '',
    // Scoped to the selected store, so the capabilities are that store's own answer —
    // the way the PDP and personalization drawer have been scoped since EA-3848.
    storeId,
    orderType: type,
  };

  // Every input, canonicalised: the ids are sorted so two orderings of the same product
  // share an entry, while the request itself keeps them in variant order.
  const cacheKey = JSON.stringify({ ...request, productIds: [...productIds].sort((a, b) => a - b) });

  const cached = _capabilityCache.get(cacheKey);
  if (cached) return cached;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);
  try {
    const response = await fetch(AVAILABILITY_API.product, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const caps = readCapabilities(await response.json());
    if (caps.size === 0) return null; // nothing usable — leave every variant selectable
    _capabilityCache.set(cacheKey, caps);
    return caps;
  } catch (err) {
    const timedOut = err && err.name === 'AbortError';
    console.warn(
      '[catering-variant-drawer] variant availability check failed:',
      timedOut ? 'request timed out' : err
    );
    return null; // fail open — see isVariantFulfillable
  } finally {
    clearTimeout(timeoutId);
  }
}

// ── Event handlers ───────────────────────────────────────────────────────────

function handleVariantSelect(variantId) {
  if (!_currentProduct) return;
  const variant = _currentProduct.variants.find((v) => v.id === variantId);
  if (!variant || !isVariantSelectable(variant)) return;

  _selectedVariantId = variantId;

  // Update card borders and name weights
  const wrapper = document.querySelector('[data-catering-variant-drawer]');
  if (!wrapper) return;

  wrapper.querySelectorAll('[data-cvd-variant]').forEach((card) => {
    const id = parseInt(card.dataset.cvdVariant, 10);
    const isSelected = id === variantId;
    // `.is-selected` drives both the border colour and the bold variant name.
    card.classList.toggle('is-selected', isSelected);
    card.setAttribute('aria-selected', String(isSelected));
  });

  // Enable ATC button — appearance is driven by the `:disabled` CSS rule.
  const btn = wrapper.querySelector('[data-cvd-add-to-cart]');
  if (btn) btn.disabled = false;
}

/**
 * Dispatch the in-memory add-to-cart event for a resolved product + variant.
 * Consumed by catering-cart.js, which owns the sticky-bar state.
 */
function dispatchAddToCart(product, variant, quantity) {
  const productImages = product.images || [];
  const image = (variant.featured_image && variant.featured_image.src)
    ? variant.featured_image.src
    : (productImages.length > 0 ? productImages[0] : '');

  document.dispatchEvent(new CustomEvent('catering:add-to-cart', {
    detail: {
      variantId: variant.id,
      productId: product.id,
      // Carried so the cart line can publish `_product_handle` at checkout — the
      // catering cart builds Storefront API lines, so nothing else recovers it.
      handle: product.handle || '',
      title: product.title,
      variantTitle: variant.title,
      price: variant.price,
      image: image,
      quantity: Math.max(1, parseInt(quantity, 10) || 1),
      maxQuantity: getVariantStock(variant),
    },
  }));
}

function resetState() {
  _currentProduct = null;
  _selectedVariantId = null;
  _originProductId = null;
  _pendingQuantity = 1;
  _inventory = null;
  _reserved = null;
  // Belongs to the product that just closed — a stale map would judge the next drawer's
  // variants against ids that aren't theirs. The cross-open cache (_capabilityCache) is
  // keyed on the ids, so nothing is re-fetched needlessly.
  _capabilities = null;
}

function handleAddToCart() {
  if (!_currentProduct || !_selectedVariantId) return;

  const variant = _currentProduct.variants.find((v) => v.id === _selectedVariantId);
  if (!variant) return;

  dispatchAddToCart(_currentProduct, variant, _pendingQuantity);

  // Close the global drawer
  if (window.GlobalDrawer && typeof window.GlobalDrawer.closeDrawer === 'function') {
    window.GlobalDrawer.closeDrawer();
  }

  resetState();
}

// ── Delegated click handler ──────────────────────────────────────────────────

function handleDocumentClick(e) {
  const wrapper = e.target.closest('[data-catering-variant-drawer]');
  if (!wrapper) return;

  // Variant card click
  const variantCard = e.target.closest('[data-cvd-variant]');
  if (variantCard) {
    const variantId = parseInt(variantCard.dataset.cvdVariant, 10);
    if (!isNaN(variantId)) handleVariantSelect(variantId);
    return;
  }

  // Add to cart button click
  const atcBtn = e.target.closest('[data-cvd-add-to-cart]');
  if (atcBtn && !atcBtn.disabled) {
    handleAddToCart();
  }
}

// ── Open drawer flow ─────────────────────────────────────────────────────────

async function openVariantDrawer(detail) {
  const { handle, productId, quantity, inventory, reserved } = detail;
  if (!handle) return;

  // Every open gets a token. Nothing serialises the clicks that get us here — a shopper
  // can tap a second card while the first is still fetching — and this function awaits
  // twice, so two runs can interleave. Each checks the token after every await and drops
  // out if a newer open has started, which stops a slow first request from finishing last
  // and opening the wrong product (or adding it) with the newer click's quantity.
  const seq = ++_openSeq;

  // Held locally until the commit point below. Writing the module-level state up front is
  // what let an interleaved run corrupt it: the second click would overwrite the first
  // click's inventory and quantity while the first was still awaiting.
  const pendingQuantity = Math.max(1, parseInt(quantity, 10) || 1);
  const localInventory = inventory || null;

  // Use cached product data when available; otherwise fetch once and cache.
  // Fetching before opening lets us skip the drawer for single-variant products.
  let product = _productCache.get(handle);

  if (!product) {
    try {
      const response = await fetch(`${storefrontRoot()}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
      product = await response.json();
      _productCache.set(handle, product);
    } catch (err) {
      console.error('[catering-variant-drawer] Failed to load product:', err);
      // A superseded run leaves the loader alone — the run that replaced it owns it now,
      // and clearAtcButtonLoading() clears every spinning button anyway.
      if (seq === _openSeq) dispatchFetchEnd();
      return;
    }
    if (seq !== _openSeq) return;
  }

  // Resolved BEFORE anything calls isVariantSelectable(), which now takes fulfillment into
  // account. That includes the single-variant fast path below, which skips the drawer
  // entirely — so gating this on variant count would let a pickup-only product be added
  // under Delivery without ever being checked. A single variant is exactly the case with
  // no list to correct afterwards, so it needs the answer most.
  //
  // The cost is one request on a path that used to be instant, but only the first time for
  // a given product + store + date + method: loadCapabilities caches on all of those, and
  // returns without a request when no fulfillment type or store is selected, or no variant
  // carries a marketplace id.
  //
  // Awaited before dispatchFetchEnd() so the ATC button keeps its spinner for the whole
  // wait — releasing it here would leave a dead gap before the drawer appears.
  const capabilities = await loadCapabilities(product, localInventory);
  if (seq !== _openSeq) return;

  // ── Commit point ──────────────────────────────────────────────────────────
  // Past every await, and confirmed to still be the current open. Only now does any of
  // this reach the shared state the render helpers and the add path read, so a run that
  // was superseded can never have touched it.
  _originProductId = productId;
  _pendingQuantity = pendingQuantity;
  _inventory = localInventory;
  _reserved = reserved || null;
  _capabilities = capabilities;
  _currentProduct = product;

  // Fetch settled (or cache hit) — clear the trigger button's loader.
  dispatchFetchEnd();

  // Single-variant product that can meet the requested quantity: add straight to
  // the sticky cart, no drawer. If it can't (tracked stock < requested qty), fall
  // through to the drawer so the customer sees the same "Only N available"
  // feedback and a disabled Add button — consistent with multi-variant products.
  if (product.variants.length === 1 && isVariantSelectable(product.variants[0])) {
    const variant = product.variants[0];
    _selectedVariantId = variant.id;
    dispatchAddToCart(product, variant, _pendingQuantity);
    resetState();
    return;
  }

  // Multi-variant: open the selector drawer.
  if (!window.GlobalDrawer || typeof window.GlobalDrawer.openDrawer !== 'function') {
    console.warn('[catering-variant-drawer] GlobalDrawer not available');
    return;
  }

  // Drawer title from the first option name (e.g. "Select size").
  

  // Auto-select the first variant that can meet the requested quantity.
  const firstSelectable = product.variants.find((v) => isVariantSelectable(v));
  _selectedVariantId = firstSelectable ? firstSelectable.id : null;

  window.GlobalDrawer.openDrawer({
    title: i18n('variant_drawer.title', 'Select size'),
    content: buildDrawerContentHtml(product, _selectedVariantId),
    drawerStyle: { width: '358px', desktopWidth: '512px' },
    titleStyle: { preset: 'h4', color: '#464646', weight: '600' },
    showBack: true,
  });
}

// ── Shared rule for other surfaces ───────────────────────────────────────────

/**
 * The quick view (catering-quick-view.js) offers the same variants as this drawer, and
 * used to judge them on `variant.available` alone — so a size this drawer struck out as
 * "Not available" / "Pickup only" for the shopper's store could still be added from the
 * quick view (EA-4222). It now asks here instead, so both surfaces give one answer.
 *
 * `evaluate` resolves to a Map of variant id -> { selectable, note, maxQuantity }. It
 * fails open exactly as this drawer does (see isVariantFulfillable): an unanswered
 * capability check leaves the stock rules in force and the method unjudged.
 */
window.EACateringVariantRules = Object.freeze({
  async evaluate(product, { inventory = null, reserved = null, quantity = 1 } = {}) {
    const capabilities = await loadCapabilities(product, inventory);
    const ctx = { inventory, reserved, quantity: Math.max(1, parseInt(quantity, 10) || 1), capabilities };
    return new Map(
      (product.variants || []).map((variant) => [
        variant.id,
        {
          selectable: isVariantSelectable(variant, ctx),
          note: variantNote(variant, ctx),
          maxQuantity: getVariantStock(variant, inventory),
        },
      ])
    );
  },
});

// ── Init ─────────────────────────────────────────────────────────────────────

function init() {
  document.addEventListener('catering:open-variant-drawer', (e) => {
    openVariantDrawer(e.detail || {});
  });

  document.addEventListener('click', handleDocumentClick);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
